<template>
  <div class="space-y-6">
    <div class="text-center">
      <SpectreHeading
        as="h2"
        size="2xl"
      >
        Two Factor Authentication
      </SpectreHeading>
      <SpectreText class="mt-2">
        Please enter your authentication code{{ showRecoveryCodeForm ? ' or recovery code' : '' }} to continue.
      </SpectreText>
    </div>

    <form
      class="space-y-6"
      @submit.prevent="submit"
    >
      <div
        v-if="!showRecoveryCodeForm"
        class="space-y-4"
      >
        <SpectreField
          required
          class="space-y-2"
          :invalid="!!form.errors.code"
        >
          <SpectreLabel>
            Authentication Code
          </SpectreLabel>
          <div class="flex justify-center">
            <SpectrePinInput
              v-model="codeDigits"
              :count="6"
              @complete="clearErrors"
            />
          </div>
          <SpectreErrorMessage>
            {{ form.errors.code }}
          </SpectreErrorMessage>
          <SpectreDescription class="text-center">
            Enter the 6 digit code from your authenticator app
          </SpectreDescription>
        </SpectreField>

        <div class="text-center">
          <SpectreButton
            type="button"
            color="tertiary"
            @click="showRecoveryCodeForm = true"
          >
            Use recovery code instead
          </SpectreButton>
        </div>
      </div>

      <div
        v-else
        class="space-y-4"
      >
        <SpectreField
          required
          class="space-y-2"
          :invalid="!!form.errors.recovery_code"
        >
          <SpectreLabel>
            Recovery Code
          </SpectreLabel>
          <SpectreInput
            v-model="form.recovery_code"
            type="text"
            placeholder="abcdef-123456"
            autofocus
            @input="clearErrors"
          />
          <SpectreErrorMessage>
            {{ form.errors.recovery_code }}
          </SpectreErrorMessage>
          <SpectreDescription>
            Enter one of your recovery codes
          </SpectreDescription>
        </SpectreField>

        <div class="text-center">
          <SpectreButton
            type="button"
            color="tertiary"
            @click="showRecoveryCodeForm = false"
          >
            Use authentication code instead
          </SpectreButton>
        </div>
      </div>

      <div class="flex space-x-4">
        <SpectreButton
          type="button"
          color="secondary"
          block
          @click="cancelChallenge"
        >
          Cancel
        </SpectreButton>
        <SpectreButton
          type="submit"
          block
          :loading="processing"
          :disabled="!canSubmit"
        >
          Verify
        </SpectreButton>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useForm, router } from '@inertiajs/vue3';
import {
  SpectreButton,
  SpectreField,
  SpectreInput,
  SpectreLabel,
  SpectreErrorMessage,
  SpectreHeading,
  SpectreText,
  SpectrePinInput, SpectreDescription,
} from '@agentsoftware/spectre-ui';
import { login as twoFactorLogin } from '@/routes/two-factor';
import { cancelTwoFactor } from '@/routes/login';

const processing = ref(false);
const showRecoveryCodeForm = ref(false);
const codeDigits = ref<string[]>([]);

const form = useForm({
  code: '',
  recovery_code: '',
});

watch(codeDigits, (val) => {
  form.code = val.join('');
});

const canSubmit = computed(() => {
  if (showRecoveryCodeForm.value) {
    return form.recovery_code.length > 0;
  }
  return form.code.length === 6;
});

const clearErrors = () => {
  form.clearErrors();
};

const submit = () => {
  if (showRecoveryCodeForm.value) {
    form.code = '';
  } else {
    form.recovery_code = '';
  }

  form.post(twoFactorLogin.url(), {
    onStart: () => {
      processing.value = true;
    },
    onError: (errors) => {
      processing.value = false;
      if (!errors.code && !errors.recovery_code) {
        form.code = '';
        form.recovery_code = '';
      }
    },
    onFinish: () => {
      processing.value = false;
    },
  });
};

const cancelChallenge = () => {
  router.post(cancelTwoFactor.url());
};
</script>
